import React, { useRef, useMemo, useEffect, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { WorldSeed, PortalState, ENVIRONMENT_COLORS, PORTAL_ANIMATION_SPEEDS } from '../types/world'

interface PortalProps {
  game: string
  color?: string
  position?: [number, number, number]
  active?: boolean
  state?: PortalState
  worldSeed?: WorldSeed
  generationProgress?: number
  onClick?: () => void
  onStateChange?: (state: PortalState) => void
}

export const Portal: React.FC<PortalProps> = ({
  game,
  color = '#FF00FF',
  position = [0, 2, -4],
  active = true,
  state = 'locked',
  worldSeed,
  generationProgress = 1.0,
  onClick,
  onStateChange
}) => {
  const portalRef = useRef<THREE.Group>(null)
  const torusRef = useRef<THREE.Mesh>(null)
  const innerRef = useRef<THREE.Mesh>(null)
  const particlesRef = useRef<THREE.Points>(null)
  const glowRef = useRef<THREE.PointLight>(null)
  
  // Track previous state for transitions
  const [currentState, setCurrentState] = useState<PortalState>(state)
  const [targetColor, setTargetColor] = useState<THREE.Color>(new THREE.Color(color))
  const [currentColor, setCurrentColor] = useState<THREE.Color>(new THREE.Color(color))
  const [rotationSpeed, setRotationSpeed] = useState(PORTAL_ANIMATION_SPEEDS.locked)
  const [targetRotationSpeed, setTargetRotationSpeed] = useState(PORTAL_ANIMATION_SPEEDS.locked)
  
  // Update from worldSeed when provided
  useEffect(() => {
    if (worldSeed?.environment) {
      const envColor = ENVIRONMENT_COLORS[worldSeed.environment]
      setTargetColor(new THREE.Color(envColor))
    }
  }, [worldSeed])
  
  // Update state from props
  useEffect(() => {
    if (state !== currentState) {
      setCurrentState(state)
      onStateChange?.(state)
      
      // Update rotation speed based on state
      switch (state) {
        case 'dormant':
          setTargetRotationSpeed(PORTAL_ANIMATION_SPEEDS.normal)
          break
        case 'generating':
          setTargetRotationSpeed(PORTAL_ANIMATION_SPEEDS.generating)
          break
        case 'locked':
          setTargetRotationSpeed(PORTAL_ANIMATION_SPEEDS.locked)
          break
        case 'active':
          setTargetRotationSpeed(PORTAL_ANIMATION_SPEEDS.active)
          break
      }
    }
  }, [state, currentState, onStateChange])
  
  // Portal ring geometry
  const torusGeometry = useMemo(() => {
    return new THREE.TorusGeometry(1, 0.08, 16, 100)
  }, [])
  
  // Inner glow disc
  const discGeometry = useMemo(() => {
    return new THREE.CircleGeometry(0.9, 64)
  }, [])
  
  // Spiral particles inside portal - updated based on state
  const [particlesGeometry, particlesMaterial] = useMemo(() => {
    const count = 200
    const positions = new Float32Array(count * 3)
    const colors = new Float32Array(count * 3)
    const velocities = new Float32Array(count) // For chaotic movement
    
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 8
      const radius = 0.1 + (i / count) * 0.7
      const height = (Math.random() - 0.5) * 0.3
      
      positions[i * 3] = Math.cos(angle) * radius
      positions[i * 3 + 1] = height
      positions[i * 3 + 2] = Math.sin(angle) * radius
      
      // Gradient from center to edge
      const t = i / count
      colors[i * 3] = targetColor.r * (1 - t * 0.5)
      colors[i * 3 + 1] = targetColor.g * (1 - t * 0.5)
      colors[i * 3 + 2] = targetColor.b
      
      velocities[i] = Math.random()
    }
    
    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3))
    geo.setAttribute('velocity', new THREE.BufferAttribute(velocities, 1))
    
    const mat = new THREE.PointsMaterial({
      size: 0.04,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending
    })
    
    return [geo, mat]
  }, [targetColor])
  
  // Update particle colors when target color changes
  useEffect(() => {
    if (particlesGeometry) {
      const colors = particlesGeometry.attributes.color.array as Float32Array
      const count = colors.length / 3
      
      for (let i = 0; i < count; i++) {
        const t = i / count
        colors[i * 3] = targetColor.r * (1 - t * 0.5)
        colors[i * 3 + 1] = targetColor.g * (1 - t * 0.5)
        colors[i * 3 + 2] = targetColor.b
      }
      
      particlesGeometry.attributes.color.needsUpdate = true
    }
  }, [targetColor, particlesGeometry])
  
  useFrame((state, delta) => {
    if (!active || !portalRef.current) return
    
    const time = state.clock.getElapsedTime()
    
    // Smooth color transition (lerp over 1s)
    const lerpFactor = Math.min(delta * 2, 1) // 0.5s transition
    currentColor.lerp(targetColor, lerpFactor)
    setCurrentColor(currentColor.clone())
    
    // Smooth rotation speed transition
    const speedLerpFactor = Math.min(delta * 1, 1)
    setRotationSpeed(prev => prev + (targetRotationSpeed - prev) * speedLerpFactor)
    
    // Convert RPM to radians per frame
    const rotationDelta = (rotationSpeed * 2 * Math.PI / 60) * delta
    
    // Portal spinning - different behavior per state
    if (torusRef.current) {
      torusRef.current.rotation.z += rotationDelta
      
      // Pulsating glow during generation
      if (currentState === 'generating') {
        const pulse = 1 + Math.sin(time * 8) * 0.1 * generationProgress
        torusRef.current.scale.setScalar(pulse)
      } else {
        torusRef.current.scale.setScalar(1)
      }
    }
    
    // Inner disc rotation (opposite direction, faster)
    if (innerRef.current) {
      innerRef.current.rotation.z -= rotationDelta * 1.6
    }
    
    // Particle animation based on state
    if (particlesRef.current) {
      const positions = particlesGeometry.attributes.position.array as Float32Array
      const velocities = particlesGeometry.attributes.velocity.array as Float32Array
      
      if (currentState === 'generating') {
        // Chaotic movement during generation
        particlesRef.current.rotation.y += rotationDelta * 0.5
        particlesRef.current.rotation.z = Math.sin(time * 3) * 0.3
        
        // Jitter particles
        for (let i = 0; i < positions.length / 3; i++) {
          const jitter = 0.02 * generationProgress
          positions[i * 3] += (Math.random() - 0.5) * jitter
          positions[i * 3 + 1] += (Math.random() - 0.5) * jitter
        }
        particlesGeometry.attributes.position.needsUpdate = true
      } else if (currentState === 'active') {
        // Particles flow toward center
        particlesRef.current.rotation.y += rotationDelta * 0.3
        
        for (let i = 0; i < positions.length / 3; i++) {
          const x = positions[i * 3]
          const z = positions[i * 3 + 2]
          const dist = Math.sqrt(x * x + z * z)
          
          if (dist > 0.1) {
            positions[i * 3] *= 0.995
            positions[i * 3 + 2] *= 0.995
          }
        }
        particlesGeometry.attributes.position.needsUpdate = true
      } else {
        // Organized spiral in locked/dormant state
        particlesRef.current.rotation.y += rotationDelta * 0.3
        particlesRef.current.rotation.z = Math.sin(time * 0.5) * 0.1
      }
    }
    
    // Scale pulse based on state
    let pulseScale = 1
    if (currentState === 'generating') {
      pulseScale = 1 + Math.sin(time * 6) * 0.08
    } else if (currentState === 'locked') {
      pulseScale = 1 + Math.sin(time * 2) * 0.03
    } else if (currentState === 'active') {
      pulseScale = 1 + Math.sin(time * 4) * 0.05
    }
    portalRef.current.scale.setScalar(pulseScale)
    
    // Update glow intensity based on state
    if (glowRef.current) {
      switch (currentState) {
        case 'dormant':
          glowRef.current.intensity = 1
          break
        case 'generating':
          glowRef.current.intensity = 2 + Math.sin(time * 8) * 0.5
          break
        case 'locked':
          glowRef.current.intensity = 2.5
          break
        case 'active':
          glowRef.current.intensity = 4
          break
      }
    }
  })
  
  if (!active) return null
  
  // Calculate opacity based on state
  const getStateOpacity = () => {
    switch (currentState) {
      case 'dormant': return 0.4
      case 'generating': return 0.9
      case 'locked': return 0.9
      case 'active': return 1.0
      default: return 0.9
    }
  }
  
  // Calculate glow color based on state
  const getGlowColor = () => {
    if (currentState === 'generating') {
      // Pulsating between white and target color
      return currentColor
    }
    return currentColor
  }
  
  return (
    <group 
      ref={portalRef} 
      position={position}
      onClick={onClick}
    >
      {/* Main portal ring */}
      <mesh ref={torusRef} geometry={torusGeometry}>
        <meshBasicMaterial 
          color={currentColor}
          transparent={true}
          opacity={getStateOpacity()}
        />
      </mesh>
      
      {/* Inner glow disc */}
      <mesh ref={innerRef} geometry={discGeometry} position={[0, 0, 0.01]}>
        <meshBasicMaterial 
          color={currentColor}
          transparent={true}
          opacity={currentState === 'generating' ? 0.6 : 0.4}
          side={THREE.DoubleSide}
        />
      </mesh>
      
      {/* Second ring for depth */}
      <mesh position={[0, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[1, 0.03, 16, 100]} />
        <meshBasicMaterial 
          color={currentState === 'generating' ? '#ffffff' : currentColor}
          transparent={true}
          opacity={currentState === 'generating' ? 0.5 : 0.3}
        />
      </mesh>
      
      {/* Spiral particles */}
      <points ref={particlesRef} geometry={particlesGeometry} material={particlesMaterial} />
      
      {/* Point light for glow effect - intensity changes with state */}
      <pointLight 
        ref={glowRef}
        color={getGlowColor()}
        intensity={currentState === 'generating' ? 3 : 2}
        distance={currentState === 'active' ? 6 : 4}
        position={[0, 0, 0.5]}
      />
      
      {/* Additional beacon light when locked */}
      {currentState === 'locked' && (
        <pointLight
          color={currentColor}
          intensity={1}
          distance={8}
          position={[0, 0, -1]}
        />
      )}
      
      {/* Game label floating above */}
      <mesh position={[0, 1.4, 0]}>
        <planeGeometry args={[1.5, 0.3]} />
        <meshBasicMaterial 
          color={currentColor}
          transparent={true}
          opacity={currentState === 'generating' ? 0.9 : 0.8}
        />
      </mesh>
      
      {/* WorldSeed info label (subtle) */}
      {worldSeed && currentState === 'locked' && (
        <mesh position={[0, -1.4, 0]}>
          <planeGeometry args={[1.2, 0.15]} />
          <meshBasicMaterial 
            color={currentColor}
            transparent={true}
            opacity={0.5}
          />
        </mesh>
      )}
    </group>
  )
}

export default Portal
